import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { makeStyles } from '@material-ui/styles';
import {
	Modal,
	Card,
	CardHeader,
	CardContent,
	CardActions,
	Divider,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Typography,
	Button,
	LinearProgress,
	Select,
	MenuItem,
	InputLabel,
	FormControl,
	FormHelperText
} from '@material-ui/core';
import MultiSelect from 'src/customComponents/MultiSelect';
import { GTKY_QUESTIONS, getLandSizeOptions } from 'src/constants/gtkyQuestions';

const useStyles = makeStyles((theme) => ({
	root: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		outline: 'none',
		boxShadow: theme.shadows[20],
		width: 800,
		maxHeight: '90vh',
		maxWidth: '100%',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden'
	},
	scrollBody: {
		flex: 1,
		overflowY: 'auto'
	},
	actions: {
		justifyContent: 'flex-end',
		flexShrink: 0,
		padding: theme.spacing(1.5, 2)
	},
	progress: {
		marginBottom: theme.spacing(1)
	},
	progressLabel: {
		display: 'flex',
		justifyContent: 'space-between',
		marginBottom: theme.spacing(0.5)
	},
	contactName: {
		color: theme.palette.primary.main,
		fontWeight: 600
	},
	mandatory: {
		color: theme.palette.error.main,
		fontSize: 11,
		marginLeft: 3
	},
	questionLabel: {
		fontWeight: 500,
		fontSize: 13
	},
	questionRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 16,
		padding: '6px 0',
		borderBottom: `1px solid ${theme.palette.divider}`
	},
	questionLabelCol: {
		flex: '0 0 38%'
	},
	questionInputCol: {
		flex: '0 0 45%'
	},
	contextNote: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: 6,
		borderBottom: `1px solid ${theme.palette.divider}`,
		padding: theme.spacing(0.75, 2)
	},
	contextNoteText: {
		color: theme.palette.text.secondary,
		fontSize: 12,
		lineHeight: 1.5,
		fontStyle: 'italic'
	}
}));

/**
 * GtkyCompletionModal
 *
 * Non-dismissible modal showing exactly 7 fixed mandatory dropdown questions.
 * Answers are mapped to the project's default GTKY survey questions by name.
 *
 * Props:
 *  - open: bool
 *  - survey: object — the default GTKY survey (from ensureGtkyQuestionsApi)
 *  - contactId: string (required in normal save mode; omit in collect-only mode)
 *  - contactName: string
 *  - projectId: string
 *  - existingAnswers: array | null — pre-populate from a previous submission
 *  - contextNote: string (optional) — info banner explaining why the popup appeared
 *  - onComplete: function — called after successful API save (normal mode)
 *  - onSubmitAnswers: function(answers, version, surveyId) — collect-only mode
 *  - onCancel: function (optional)
 *  - cancelLabel: string (optional)
 *  - cancelConfirmMessage: string (optional) — shows confirm dialog before calling onCancel
 *  - onSkipSurvey: function (optional) — proceed without saving GTKY answers
 *  - skipLabel: string (optional, default 'Skip Survey')
 *  - allMandatory: bool (default true) — when false, all questions are optional
 *  - progressCurrent / progressTotal: number (bulk progress indicator)
 */
function GtkyCompletionModal({
	open,
	survey,
	contactId,
	contactName,
	projectId,
	existingAnswers,
	contextNote,
	onComplete,
	onSubmitAnswers,
	onCancel,
	cancelLabel,
	cancelConfirmMessage,
	onSkipSurvey,
	skipLabel = 'Skip Survey',
	allMandatory = true,
	progressCurrent,
	progressTotal
}) {
	const classes = useStyles();
	// selections[i] corresponds to GTKY_QUESTIONS[i]
	const [selections, setSelections] = useState(GTKY_QUESTIONS.map(() => ''));
	const [errorIndices, setErrorIndices] = useState([]);
	const [saveError, setSaveError] = useState(null);
	const [saving, setSaving] = useState(false);
	const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

	const surveyQuestions = survey?.versions?.[0]?.questions || [];
	const version = survey?.versions?.[0]?.version || 1;
	const showProgress = progressTotal && progressTotal > 1;

	// Build a lookup: surveyKey (lowercase) → survey question (for type + options)
	const surveyQuestionByKey = {};
	surveyQuestions.forEach((sq) => {
		if (sq.question) surveyQuestionByKey[sq.question.toLowerCase()] = sq;
	});

	const getSurveyQ = (gtkyQ) => surveyQuestionByKey[gtkyQ.surveyKey.toLowerCase()];

	// Land Size: always prepend 130m2 - 300m2, then survey options
	const landSizeQ = getSurveyQ(GTKY_QUESTIONS[6]);
	const surveyLandSizeOptions = landSizeQ?.options || [];
	const landSizeOptions = getLandSizeOptions(surveyLandSizeOptions);

	// Pre-populate selections from existingAnswers when modal opens
	useEffect(() => {
		if (!open) return;
		const initial = GTKY_QUESTIONS.map((gtkyQ) => {
			// Find index of this question in survey questions
			const surveyIdx = surveyQuestions.findIndex(
				(sq) => sq.question?.toLowerCase() === gtkyQ.surveyKey.toLowerCase()
			);
			return (surveyIdx >= 0 && existingAnswers?.[surveyIdx]?.value) || '';
		});
		setSelections(initial);
		setErrorIndices([]);
		setSaveError(null);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, survey, existingAnswers]);

	const handleSelect = (gtkyIndex, value) => {
		const updated = [...selections];
		updated[gtkyIndex] = value;
		setSelections(updated);
		// Clear error for this field on change
		setErrorIndices((prev) => prev.filter((i) => i !== gtkyIndex));
	};

	// Build the answers[] array in survey question order for API submission
	const buildAnswersArray = () => {
		return surveyQuestions.map((sq, i) => {
			const gtkyIdx = GTKY_QUESTIONS.findIndex(
				(gtkyQ) => gtkyQ.surveyKey.toLowerCase() === sq.question?.toLowerCase()
			);
			return {
				id: i,
				value: gtkyIdx >= 0 ? selections[gtkyIdx] : null,
				isMandatory: allMandatory && gtkyIdx >= 0
			};
		});
	};

	const validate = () => {
		if (!allMandatory) return true;

		const missing = GTKY_QUESTIONS.map((_, i) => i).filter((i) => {
			const val = selections[i];
			if (val === null || val === undefined || val === '') return true;
			if (Array.isArray(val) && val.length === 0) return true;
			return false;
		});
		setErrorIndices(missing);
		return missing.length === 0;
	};

	const hasAnyGtkyAnswer = () =>
		selections.some((val) => {
			if (Array.isArray(val)) return val.length > 0;
			return val !== null && val !== undefined && val !== '';
		});

	const onSave = async () => {
		if (!validate()) return;

		const surveyId = survey?.id;
		if (!surveyId) {
			setSaveError('Survey not available. Please reload and try again.');
			return;
		}

		const answers = buildAnswersArray();
		const isCollectOnly = Boolean(onSubmitAnswers);

		setSaveError(null);

		// Optional flows: no answers filled → skip GTKY and proceed
		if (!allMandatory && !hasAnyGtkyAnswer()) {
			if (onSkipSurvey) {
				onSkipSurvey();
				return;
			}
			if (isCollectOnly) {
				onSubmitAnswers(answers, version, surveyId);
				return;
			}
			onComplete?.();
			return;
		}

		if (isCollectOnly) {
			onSubmitAnswers(answers, version, surveyId);
			return;
		}

		setSaving(true);
		try {
			try {
				await axios.post(`/api/projects/${projectId}/survey/assign`, {
					rest: { surveyId, contactId }
				});
			} catch (assignErr) {
				if (assignErr?.response?.status !== 409) throw assignErr;
			}
			await axios.post(
				`/api/projects/${projectId}/survey/${surveyId}/answers`,
				{ answers, surveyId, projectId, contactId, version: String(version) }
			);
			onComplete();
		} catch (e) {
			setSaveError('Failed to save. Please try again.');
		} finally {
			setSaving(false);
		}
	};

	if (!open || !survey) return null;

	const title = showProgress
		? `GTKY Profile — Contact ${progressCurrent} of ${progressTotal}`
		: 'GTKY Profile';

	return (
		<>
		<Modal open={open} disableBackdropClick disableEscapeKeyDown>
			<Card className={classes.root}>

				{/* ── Fixed header ── */}
				<CardHeader
					title={title}
					titleTypographyProps={{ variant: 'h6' }}
					subheader={
						contactName ? (
							<span className={classes.contactName}>{contactName}</span>
						) : null
					}
					style={{ paddingBottom: 8, paddingTop: 12 }}
				/>
				<Divider />

				{contextNote && (
					<div className={classes.contextNote}>
						<Typography className={classes.contextNoteText}>
							ℹ️ {contextNote}
						</Typography>
					</div>
				)}

				{showProgress && (
					<div style={{ padding: '8px 16px 0' }}>
						<div className={classes.progressLabel}>
							<Typography variant="caption">Completing GTKY profiles</Typography>
							<Typography variant="caption">{progressCurrent}/{progressTotal}</Typography>
						</div>
						<LinearProgress
							className={classes.progress}
							variant="determinate"
							value={((progressCurrent - 1) / progressTotal) * 100}
						/>
					</div>
				)}

				{/* ── Scrollable body ── */}
				<div className={classes.scrollBody}>
					<CardContent style={{ paddingTop: 10, paddingBottom: 6 }}>

						{allMandatory && errorIndices.length > 0 && (
							<Typography color="error" variant="caption" display="block" gutterBottom>
								Please complete all required fields marked with *.
							</Typography>
						)}

						{saveError && (
							<Typography color="error" variant="caption" display="block" gutterBottom>
								{saveError}
							</Typography>
						)}

						<div style={{ marginTop: 4 }}>
							{GTKY_QUESTIONS.map((gtkyQ, i) => {
								const options = gtkyQ.options !== null
									? gtkyQ.options
									: landSizeOptions;
								const hasError = errorIndices.includes(i);
								const sq = getSurveyQ(gtkyQ);
								const isMulti = sq?.type === 'multi-select';
								return (
									<div key={gtkyQ.surveyKey} className={classes.questionRow}>
										<div className={classes.questionLabelCol}>
											<Typography variant="body2" className={classes.questionLabel}>
												{gtkyQ.label}
												{allMandatory && (
													<span className={classes.mandatory}>*</span>
												)}
											</Typography>
										</div>
										<div className={classes.questionInputCol}>
											{isMulti ? (
												<MultiSelect
													title="Select answers"
													data={options}
													name={gtkyQ.surveyKey}
													selectedValue={[]}
													value={Array.isArray(selections[i]) ? selections[i] : []}
													callBack={(e, v) => handleSelect(i, v)}
													error={hasError}
												/>
											) : (
												<FormControl fullWidth error={hasError}>
													<InputLabel>Select answer</InputLabel>
													<Select
														value={selections[i] || ''}
														onChange={(e) => handleSelect(i, e.target.value)}
														disabled={options.length === 0}
													>
														{options.map((opt) => (
															<MenuItem key={opt} value={opt}>
																{opt}
															</MenuItem>
														))}
													</Select>
													{allMandatory && hasError && (
														<FormHelperText>Required</FormHelperText>
													)}
												</FormControl>
											)}
										</div>
									</div>
								);
							})}
						</div>

						{allMandatory && (
							<Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
								<span style={{ color: '#f44336', fontWeight: 600 }}>*</span>
								{' '}All fields are required
							</Typography>
						)}
					</CardContent>
				</div>

				{/* ── Sticky footer ── */}
				<Divider />
				<CardActions className={classes.actions}>
					{onCancel && (
						<Button
							size="small"
							onClick={() => {
								if (cancelConfirmMessage) {
									setConfirmCancelOpen(true);
								} else {
									onCancel();
								}
							}}
							disabled={saving}
						>
							{cancelLabel || 'Cancel'}
						</Button>
					)}
					{!allMandatory && onSkipSurvey && (
						<Button
							size="small"
							onClick={onSkipSurvey}
							disabled={saving}
						>
							{skipLabel}
						</Button>
					)}
					<Button
						size="small"
						color="primary"
						variant="contained"
						onClick={onSave}
						disabled={saving}
					>
						{saving
							? 'Saving...'
							: showProgress && progressCurrent < progressTotal
							? 'Submit & Continue →'
							: 'Submit'}
					</Button>
				</CardActions>
			</Card>

		</Modal>

		{/* Confirm-cancel dialog — outside Modal to avoid MUI "single child" constraint */}
		<Dialog
			open={confirmCancelOpen}
			onClose={() => setConfirmCancelOpen(false)}
		>
			<DialogTitle>Are you sure?</DialogTitle>
			<DialogContent>
				<DialogContentText>{cancelConfirmMessage}</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={() => setConfirmCancelOpen(false)}
					color="primary"
					variant="contained"
				>
					Continue Filling
				</Button>
				<Button
					onClick={() => {
						setConfirmCancelOpen(false);
						onCancel();
					}}
				>
					Exit Without Saving
				</Button>
			</DialogActions>
		</Dialog>
		</>
	);
}

GtkyCompletionModal.propTypes = {
	open: PropTypes.bool.isRequired,
	survey: PropTypes.object,
	contactId: PropTypes.string,
	contactName: PropTypes.string,
	projectId: PropTypes.string,
	existingAnswers: PropTypes.array,
	contextNote: PropTypes.string,
	onComplete: PropTypes.func,
	onSubmitAnswers: PropTypes.func,
	onCancel: PropTypes.func,
	cancelLabel: PropTypes.string,
	cancelConfirmMessage: PropTypes.string,
	onSkipSurvey: PropTypes.func,
	skipLabel: PropTypes.string,
	allMandatory: PropTypes.bool,
	progressCurrent: PropTypes.number,
	progressTotal: PropTypes.number
};

export default GtkyCompletionModal;
